import { useState } from "react";
import { enableLocalVaultEncryption, unlockLocalVault, disableLocalVaultEncryption, clearLocalVaultSession } from "../utils/localVaultSecurity";
import { setSecureItem, removeSecureItem, migrateLegacyJsonItem, readLegacyString, removeLegacyItem } from "../utils/secureStore";

const PIN_RECORD_KEY  = "finvault_pin_record_v2";
const PIN_RECORD_STORE_KEY = "pin.record.v2";
const PIN_KEY_LEGACY  = "finvault_pin_hash";
const PIN_ENABLED_KEY = "finvault_pin_enabled";
const PIN_SALT_LEGACY = "finvault_2024_salt";
const PIN_ATTEMPTS_KEY = "finvault_pin_failed_attempts";
const PIN_LOCK_UNTIL_KEY = "finvault_pin_lock_until";
const PIN_ITERATIONS = 210000;
export const NEW_PIN_LENGTH = 6;
export const LEGACY_PIN_LENGTH = 4;

export function isSupportedPinLength(pin) {
  return [LEGACY_PIN_LENGTH, NEW_PIN_LENGTH].includes(String(pin || "").length);
}

export function isValidNewPin(pin) {
  return new RegExp(`^\\d{${NEW_PIN_LENGTH}}$`).test(String(pin || ""));
}

const enc = new TextEncoder();

function bytesToBase64(bytes) {
  let bin = "";
  bytes.forEach(b => { bin += String.fromCharCode(b); });
  return btoa(bin);
}

function base64ToBytes(value = "") {
  const bin = atob(value);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function randomBase64(length = 16) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytesToBase64(bytes);
}

async function sha256(str) {
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(str));
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("");
}

async function derivePinHash(pin, salt, iterations = PIN_ITERATIONS) {
  const material = await crypto.subtle.importKey("raw", enc.encode(String(pin || "")), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: base64ToBytes(salt), iterations, hash: "SHA-256" },
    material,
    256
  );
  return bytesToBase64(new Uint8Array(bits));
}

let pinRecordCache = null;

async function getPinRecord() {
  if (pinRecordCache) return pinRecordCache;
  const record = await migrateLegacyJsonItem(PIN_RECORD_STORE_KEY, PIN_RECORD_KEY);
  pinRecordCache = record || null;
  return pinRecordCache;
}

async function setPinRecord(record) {
  pinRecordCache = record || null;
  await setSecureItem(PIN_RECORD_STORE_KEY, pinRecordCache);
}

async function removePinRecord() {
  pinRecordCache = null;
  await removeSecureItem(PIN_RECORD_STORE_KEY);
  removeLegacyItem(PIN_RECORD_KEY);
}

function clearAttempts() {
  localStorage.removeItem(PIN_ATTEMPTS_KEY);
  localStorage.removeItem(PIN_LOCK_UNTIL_KEY);
}

function registerFailedAttempt() {
  const attempts = parseInt(localStorage.getItem(PIN_ATTEMPTS_KEY) || "0", 10) + 1;
  localStorage.setItem(PIN_ATTEMPTS_KEY, String(attempts));
  if (attempts >= 5) {
    const minutes = Math.min(15, 1 + Math.floor((attempts - 5) / 2) * 2);
    localStorage.setItem(PIN_LOCK_UNTIL_KEY, String(Date.now() + minutes * 60 * 1000));
  }
}

export function getPinLockRemainingSeconds() {
  const until = parseInt(localStorage.getItem(PIN_LOCK_UNTIL_KEY) || "0", 10);
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

export function isPinEnabled() {
  return localStorage.getItem(PIN_ENABLED_KEY) === "true" || Boolean(readLegacyString(PIN_KEY_LEGACY));
}

export function getPinSecurityStatus() {
  return isPinEnabled()
    ? { strong: true, kdf: "PBKDF2-SHA256", iterations: PIN_ITERATIONS, storage: "IndexedDB secure store", newPinLength: NEW_PIN_LENGTH }
    : { strong: false, kdf: "none", iterations: 0, storage: "none" };
}

export async function setPin(pin, { allowLegacyLength = false } = {}) {
  const normalizedPin = String(pin || "");
  if (!isValidNewPin(normalizedPin) && !(allowLegacyLength && /^\d{4}$/.test(normalizedPin))) {
    throw new Error(`PIN must contain exactly ${NEW_PIN_LENGTH} digits.`);
  }
  const salt = randomBase64(16);
  const hash = await derivePinHash(normalizedPin, salt, PIN_ITERATIONS);
  await setPinRecord({
    v: 2,
    kdf: "PBKDF2-SHA256",
    iterations: PIN_ITERATIONS,
    pinLength: normalizedPin.length,
    salt,
    hash,
    updatedAt: new Date().toISOString()
  });
  removeLegacyItem(PIN_KEY_LEGACY);
  localStorage.setItem(PIN_ENABLED_KEY, "true");
  clearAttempts();
  await enableLocalVaultEncryption(normalizedPin);
}

export async function removePin() {
  await removePinRecord();
  removeLegacyItem(PIN_KEY_LEGACY);
  localStorage.setItem(PIN_ENABLED_KEY, "false");
  clearAttempts();
  await disableLocalVaultEncryption();
  clearLocalVaultSession();
}

export async function verifyPin(pin) {
  const remaining = getPinLockRemainingSeconds();
  if (remaining > 0) return { ok: false, locked: true, remaining };
  if (!isSupportedPinLength(pin) || !/^\d+$/.test(String(pin || ""))) {
    return { ok: false, locked: false, error: "Enter your complete PIN." };
  }
  const record = await getPinRecord();
  let ok = false;
  if (record?.v === 2 && record?.salt && record?.hash) {
    const hash = await derivePinHash(pin, record.salt, record.iterations || PIN_ITERATIONS);
    ok = hash === record.hash;
  } else {
    const legacyHash = await sha256(PIN_SALT_LEGACY + pin);
    ok = readLegacyString(PIN_KEY_LEGACY) === legacyHash;
    if (ok) await setPin(pin, { allowLegacyLength: true });
  }
  if (ok) {
    clearAttempts();
    try { await unlockLocalVault(pin); } catch { return { ok: false, locked: false, error: "Could not unlock encrypted local vault." }; }
    return { ok: true };
  }
  registerFailedAttempt();
  return { ok: false, locked: false, remaining: getPinLockRemainingSeconds() };
}

// ── FEATURE 5: Auto-lock timer ───────────────────────────────
const LAST_ACTIVE_KEY  = "finvault_last_active";
const AUTO_LOCK_KEY    = "finvault_auto_lock_mins";
export function getAutoLockMins() { return parseInt(localStorage.getItem(AUTO_LOCK_KEY)||"5"); }
export function setAutoLockMins(n) { localStorage.setItem(AUTO_LOCK_KEY, String(n)); }

export function updateLastActive() {
  localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
}
export function shouldAutoLock() {
  if (!isPinEnabled()) return false;
  const last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY)||"0");
  if (!last) return false;
  return (Date.now() - last) > getAutoLockMins() * 60 * 1000;
}

export default function PinLock({ mode = "verify", onSuccess, onCancel }) {
  const [pin, setPin2]    = useState("");
  const [confirm, setCfm] = useState("");
  const [step, setStep]   = useState(1);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(false);

  const doShake = () => { setShake(true); setTimeout(() => setShake(false), 500); };

  const submitVerification = async (candidate = pin) => {
    if (!isSupportedPinLength(candidate)) {
      setError("Enter your complete 4- or 6-digit PIN.");
      doShake();
      return;
    }
    setChecking(true);
    const result = await verifyPin(candidate);
    setChecking(false);
    if (result?.ok) {
      updateLastActive();
      onSuccess();
      return;
    }
    const msg = result?.locked
      ? `Too many wrong attempts. Try again in ${Math.ceil((result.remaining || 60) / 60)} min.`
      : result?.error || (result?.remaining > 0 ? `Wrong PIN. Temporarily locked for ${Math.ceil(result.remaining / 60)} min.` : "Wrong PIN. Try again.");
    setError(msg);
    doShake();
    setPin2("");
  };

  const handleKey = async (k) => {
    setError("");
    if (k === "del") {
      if (step === 1) setPin2(p => p.slice(0,-1));
      else setCfm(p => p.slice(0,-1));
      return;
    }
    const cur = step === 1 ? pin : confirm;
    const maxLength = NEW_PIN_LENGTH;
    if (cur.length >= maxLength) return;
    const next = cur + k;

    if (step === 1) {
      setPin2(next);
      if (mode !== "verify" && next.length === NEW_PIN_LENGTH) {
        setTimeout(() => setStep(2), 200);
      }
    } else {
      setCfm(next);
      if (next.length === NEW_PIN_LENGTH) {
        if (next === pin) {
          await setPin(next);
          onSuccess(next);
        } else {
          setError("PINs don't match. Try again.");
          doShake(); setPin2(""); setCfm(""); setStep(1);
        }
      }
    }
  };

  const keys = ["1","2","3","4","5","6","7","8","9","","0","del"];
  const cur   = step === 1 ? pin : confirm;
  const isLocalMode = localStorage.getItem("finvault_storage_mode") === "local";

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:500,
      background:"linear-gradient(160deg,#1E1B4B 0%,#312E81 100%)",
      display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", fontFamily:"'Nunito',sans-serif"
    }}>
      <div style={{fontSize:48, marginBottom:8}}>🔐</div>
      <div style={{fontSize:22, fontWeight:900, color:"white", marginBottom:4}}>
        {mode === "verify" ? "Enter PIN" : step === 1 ? "Set New PIN" : "Confirm PIN"}
      </div>
      <div style={{fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:isLocalMode ? 12 : 32, textAlign:"center", padding:"0 18px", lineHeight:1.45}}>
        {mode === "verify" ? "Enter your PIN to unlock (legacy 4-digit PINs remain supported)" :
         step === 1 ? `Choose a ${NEW_PIN_LENGTH}-digit PIN` : "Re-enter your PIN to confirm"}
      </div>

      {isLocalMode && (
        <div style={{width:300,maxWidth:"calc(100vw - 40px)",background:"rgba(255,255,255,0.10)",border:"1px solid rgba(255,255,255,0.16)",borderRadius:16,padding:"10px 12px",marginBottom:20,backdropFilter:"blur(6px)"}}>
          <div style={{fontSize:11.5,color:"#FDE68A",fontWeight:900,marginBottom:3}}>Local Mode recovery notice</div>
          <div style={{fontSize:10.8,color:"rgba(255,255,255,0.68)",fontWeight:700,lineHeight:1.45}}>
            If this PIN is forgotten, encrypted local data cannot be recovered by email. Restore from your JSON backup or reset the local vault.
          </div>
        </div>
      )}

      {/* Dots */}
      <div style={{
        display:"flex", gap:16, marginBottom:12,
        animation: shake ? "shake 0.4s ease" : "none"
      }}>
        {Array.from({ length: NEW_PIN_LENGTH }, (_, i) => (
          <div key={i} style={{
            width:18, height:18, borderRadius:"50%",
            background: i < cur.length ? "white" : "rgba(255,255,255,0.2)",
            transition:"background 0.15s",
            border:"2px solid rgba(255,255,255,0.3)"
          }}/>
        ))}
      </div>

      {error && <div style={{fontSize:12,color:"#F87171",fontWeight:700,marginBottom:12}}>{error}</div>}
      {checking && <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",marginBottom:12}}>Checking…</div>}
      {!error && !checking && <div style={{height:20,marginBottom:12}}/>}

      {/* Keypad */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, width:240}}>
        {keys.map((k,i) => (
          k === "" ? <div key={i}/> :
          <button key={i} onClick={() => handleKey(k)}
            disabled={checking}
            style={{
              height:68, borderRadius:18,
              background: k === "del" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.12)",
              border:"1px solid rgba(255,255,255,0.1)",
              color:"white", fontSize: k === "del" ? 20 : 24,
              fontWeight:800, cursor:"pointer", fontFamily:"inherit",
              transition:"all 0.1s", backdropFilter:"blur(4px)",
              opacity: checking ? 0.5 : 1
            }}>
            {k === "del" ? "⌫" : k}
          </button>
        ))}
      </div>

      {mode === "verify" && (
        <button
          type="button"
          onClick={() => submitVerification()}
          disabled={checking || !isSupportedPinLength(pin)}
          style={{
            width:240,
            marginTop:14,
            padding:"12px 16px",
            border:"1px solid rgba(255,255,255,0.24)",
            borderRadius:14,
            background:isSupportedPinLength(pin) ? "white" : "rgba(255,255,255,0.12)",
            color:isSupportedPinLength(pin) ? "#312E81" : "rgba(255,255,255,0.45)",
            fontFamily:"inherit",
            fontWeight:900,
            cursor:isSupportedPinLength(pin) ? "pointer" : "not-allowed"
          }}>
          Unlock
        </button>
      )}

      {onCancel && (
        <button onClick={onCancel}
          style={{marginTop:24,background:"none",border:"none",color:"rgba(255,255,255,0.4)",
          fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>
          Cancel
        </button>
      )}
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}`}</style>
    </div>
  );
}
